import { format } from "url";
import { ISite } from "../interfaces";

/**
 * Replaces characters in a string with a specified replacement string.
 * @param {string} arr - The input string to process.
 * @param {string} [replaceWith="-"] - The string to replace the characters with.
 * @param {string[]} [charsToReplace=[" "]] - An array of characters to replace.
 * @returns {string} - The resulting string with replaced characters.
 */
export function replaceChars(arr, replaceWith: string = "-", charsToReplace: string[] = [" "]) {
  const result = arr.split(charsToReplace.pop()).join(replaceWith);

  if (charsToReplace.length > 0) {
    replaceChars(result, replaceWith, charsToReplace);
  }

  return result;
}

/**
 * Formats the page path based on the page object.
 * @param {Object} page - The page object containing slug, name, and category properties.
 * @returns {string} - The formatted page path.
 */
export function formatPagePath(page) {
  try {
    if (page.data) {
      page = page.data.attributes;
    }
    let { slug, name } = page;
    let path = slug != null ? slug : name;

    const parts: string[] = [path];

    if (page.category?.data != null) {
      parts.unshift(page.category.data?.attributes?.name);
    }

    parts.unshift("");
    path = parts.join("/");

    return `${replaceChars(path, "-", [" ", "_"]).toLowerCase()}`;
  } catch (e) {
    return "/";
  }
}

/**
 * Sorts an array of objects by the specified key and values.
 * @template T - The type of elements in the collection array.
 * @param {T[]} collection - The array of objects to be sorted.
 * @param {string} key - The property key by which to sort the objects.
 * @param {any[]} values - The array of values to sort by.
 * @returns {T[]} - The sorted array of objects.
 */

export function sortByKeyAndValues<T>(collection: T[], key: string, values: any[]): T[] {
  const collectionCopy = [...collection]; // shallow copy
  const item = collectionCopy.shift() || {};
  const val = item[key];
  const valIndex = values.indexOf(val);

  if (valIndex >= 0) {
    values.splice(valIndex, 1, item);
  } else {
    values.push(item);
  }

  if (collectionCopy.length > 0) {
    values = sortByKeyAndValues(collectionCopy, key, values);
  }

  return values.filter((filterVal) => {
    return filterVal[key] != undefined;
  });
}

/**
 * Sorts an array of objects by the specified key in ascending order.
 * @param {Array} data - The array of objects to be sorted.
 * @param {string} key - The property key by which to sort the objects.
 * @returns {Array} - The sorted array of objects.
 */
export const sortByKey = (data: any[], key: string): any[] => {
  return data.sort((a, b) => {
    const valueA = a[key].toUpperCase();
    const valueB = b[key].toUpperCase();
    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }
    return 0;
  });
};

// Takes in a collection of objects and attempts to modify the numerical values of a key/value
// compared to a base total.
// Example:  I have 3 objects that each have a width based on a css grid max of 12 (col-12).  If the
//           sum of the input widths are great than 12, this will attempt to fit them to a sum of 12.
//           [5,5,6,7,7] will result in [5,4,1,1,1]
export function normalizeForTotal<T>(
  items: T[],
  key: string,
  prefix: string | null = null,
  base: number = 12
): T[] {
  let total: number = 0;
  const totalItems: number = items.length;

  items.forEach((item, index: number) => {
    if (item != null) {
      const val = parseInt(item[key]);
      const nextTotal = total + val;
      const itemsLeft = totalItems - (index + 1);
      const maxTotalThisItem = base - itemsLeft;

      if (nextTotal > maxTotalThisItem) {
        const adjustedValue = maxTotalThisItem - total;
        items[index][key] = prefix == null ? adjustedValue : `${prefix}${adjustedValue}`;
        total = total + adjustedValue;
      } else {
        total = nextTotal;
      }
    }
  });

  return items;
}

/**
 * Checks if all objects in an array have a value for the specified key.
 * @template T - The type of objects in the array.
 * @param {T[]} objects - The array of objects to check.
 * @param {string} key - The key to check for a value in each object.
 * @returns {boolean} - True if all objects have a value for the key, false otherwise.
 */
export function doAllObjectsHaveValue<T>(objects: T[], key: string): boolean {
  for (let obj of objects) {
    if (obj[key] == null) {
      return false;
    }
  }
  return true;
}

/**
 * Generates a site class name based on the site's name and ID.
 * @param {ISite} site - The site object containing the site's name and ID.
 * @returns {string} - The generated site class name.
 */
export function getSiteClassName(site: ISite) {
  return `${site.site_name}-${site.id}`;
}

/**
 * Generates a globally unique identifier (GUID).
 * @returns {string} - The generated GUID.
 */
export function guid() {
  // I generate the UID from two parts here
  // to ensure the random number provide enough bits.
  const firstPart = (Math.random() * 46656) | 0;
  const secondPart = (Math.random() * 46656) | 0;
  const totalFirstPart = ("000" + firstPart.toString(36)).slice(-3);
  const totalSecondPart = ("000" + secondPart.toString(36)).slice(-3);
  return totalFirstPart + totalSecondPart;
}

/**
 * Converts a hyphen-separated string to camel case.
 * @param {string} str - The input string to be converted.
 * @returns {string} - The camel case formatted string.
 */
const formatStringToCamelCase = (str) => {
  const splitted = str.split("-");
  if (splitted.length === 1) return splitted[0];
  return (
    splitted[0] +
    splitted
      .slice(1)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join("")
  );
};

/**
 * Converts a string containing CSS styles into a style object.
 * @param {string} str - The string containing CSS styles.
 * @returns {Object.<string, string>} - The style object with style properties and values.
 */
export const getStyleObjectFromString = (str: string): { [symbol: string]: string } => {
  const style = {};
  str.split(";").forEach((el) => {
    const [property, value] = el.split(":");
    if (value != null) {
      const formattedProperty = formatStringToCamelCase(property.trim());
      style[formattedProperty] = value.trim();
    }
  });

  return style;
};

/**
 * Serializes an object into a URL-encoded query string.
 * @param {Object} obj - The object to be serialized.
 * @returns {string} - The URL-encoded query string.
 */
export const serialize = (obj) => {
  var str: any[] = [];
  for (var p in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(p)) {
      const param = [encodeURIComponent(p), "=", encodeURIComponent(obj[p])];
      str.push(param.join(""));
    }
  }
  return str.join("&");
};

/**
 * Applies transformations to an image source URL.
 * @param {string} src - The source URL of the image.
 * @param {Object} [transformations={}] - The transformations to be applied.
 * @returns {string} - The transformed image source URL.
 */
export const applyTransformations = (src, transformations = {}) => {
  return src?.substr(src?.length - 4) === ".gif"
    ? src
    : `${src}?${serialize({ auto: "format,compress", ...transformations })}`;
};

/**
 * Replaces non-alphabetic characters, multiple spaces, and slashes with hyphens,
 * and converts the resulting string to lowercase.
 * @param {string} string - The string to be parameterized.
 * @returns {string} - The parameterized string.
 */
export const parameterize = (string) => {
  if (string == null) return "";
  return string
    .replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "")
    .replace(/[^a-zA-Z]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[\s/]+/g, "-")
    .replace(/[-]+/g, "-")
    .toLowerCase();
};

//convert decimal to formatted number
export function formatNumber(num: number, decimalPlaces: number = 2) {
  return num.toFixed(decimalPlaces).replace(/\d(?=(\d{3})+\.)/g, "$&,");
}

export function convertToHumanReadable(value) {
  if (!value) return value;

  if (value.toLowerCase() === "redi-rock") {
    return "Redi-Rock";
  }

  // Replace underscores and dashes with spaces
  let formatted = value.replace(/[_-]/g, " ");

  // Split the string into words, capitalize the first letter of each word, and join them back
  formatted = formatted
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return formatted;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric"
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
}

/**
 * Group like siblings in a multidimensional array.
 *
 * This function takes an array of objects and groups adjacent objects with the
 * same 'component' property into sub-arrays. Objects with unique 'component'
 * values in their immediate vicinity are left as individual elements.
 * The order of elements in the original array is preserved in the output.
 *
 * @param {Object[]} arr - The array of objects to be grouped. Each object should have at least a 'component' property.
 * @returns {Array} A multidimensional array where adjacent objects with the same 'component' are grouped together.
 *
 * @example
 * // returns [{'id': 1, 'component': 'hero'}, [{'id': 2, 'component': 'icon-row'}, {'id': 3, 'component': 'icon-row'}], {'id': 4, 'component': 'hero'}]
 * groupSectionsByComponentType([
 *   { id: 1, component: "hero" },
 *   { id: 2, component: "icon-row" },
 *   { id: 3, component: "icon-row" },
 *   { id: 4, component: "hero" },
 * ]);
 */
export function groupSectionsByComponentType(arr: any = []) {
  if (!arr || arr.length === 0) return [];

  const result: any[] = [];
  let currentGroup = [arr[0]];

  for (let i = 1; i < arr.length; i++) {
    if (arr[i].__typename === arr[i - 1].__typename) {
      currentGroup.push(arr[i]);
    } else {
      if (currentGroup.length > 1) {
        result.push(currentGroup);
      } else {
        result.push(...currentGroup);
      }
      currentGroup = [arr[i]];
    }
  }

  if (currentGroup.length > 1) {
    result.push(currentGroup);
  } else {
    result.push(...currentGroup);
  }

  return result;
}

// Utility function to convert string to title case
export const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const sortByPriority = (array) => {
  return array.sort((a, b) => {
    const aPriority = a.priority ? a.priority : 11;
    const bPriority = b.priority ? b.priority : 11;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    return a.label.localeCompare(b.label);
  });
};

export const sortGroupsByPriority = (array) => {
  return array.sort((a, b) => {
    const aPriority = a.priority ? a.priority : 11;
    const bPriority = b.priority ? b.priority : 11;

    return aPriority - bPriority;
  });
};
